<script>

import { onMount } from "svelte";
import * as bootstrap from "bootstrap";
import { WBK, simplifySparqlResults, minimizeSimplifiedSparqlResults } from "wikibase-sdk";
import { basicSetup } from "codemirror";
import { EditorView, ViewUpdate, lineNumbers } from "@codemirror/view";
import { bracketMatching } from "@codemirror/language";
import { EditorState } from "@codemirror/state";
import { sparql } from "codemirror-lang-sparql";

import { randomString } from './QueryEditor/Utils.js';
import QueryResult from "./QueryEditor/QueryResult.svelte";

const wdk = WBK({
    instance: 'https://www.wikidata.org',
    sparqlEndpoint: 'https://query.wikidata.org/sparql',
})

let props = $props();

let allQueries = $state(props.queries);

let editor = null;
let running = $state(false);
let requestController = null;

let currentQuery = $state({
    id: '',
    title: '',
    description: '',
    query: '',
    active: true,
    local: true,
});

let currentNewQuery = $state({
    id: randomString(10),
    title: 'Unnamed query',
    description: 'Undescribed query',
    query: '',
    active: true,
    local: true,
});

let currentResults = $state([]);
let currentError = $state(null);

let copyModal = null;
let newModal = null;
let modalLink = $state('');

let editing = (document.getElementById('store_url') != null);
let saving = $state(false);

function selectQuery(q)
{
    allQueries.forEach((item) => {
        item.active = false;
    });

    currentQuery = q;
    currentResults = [];

    /*
        This is used to replace contents of CodeMirror widget with the text of
        the selected query
    */
    editor.dispatch({
        changes: {
            from: 0,
            to: editor.state.doc.length,
            insert: q.query,
        }
    });

    q.active = true;
}

function internalFormData()
{
    let data = new FormData();
    data.append('_token', document.querySelector('meta[name=csrf-token]').getAttribute('content'));
    return data;
}

async function executeQuery()
{
    running = true;
    const url = wdk.sparqlQuery(currentQuery.query);
    currentError = null;

    requestController = new AbortController();

    currentResults = fetch(url, {
        headers: {
            Accept: "application/sparql-results+json"
        },
        signal: requestController.signal,
    })
    .then(async (res) => {
        if (res.status == 200) {
            currentError = null;
            return res.json();
        }
        else {
            let errortext = await res.text();
            currentError = errortext.replace(/\r?\n|\r/g, '<br>');
            return [];
        }
    })
    .catch((err) => {
        if (err.name != 'AbortError') {
            currentError = err;
            return [];
        }
        else {
            currentError = null;
            return [];
        }
    })
    .finally(() => {
        running = false;
    });
}

function stopExecution()
{
    if (requestController) {
        requestController.abort();
    }
}

function pushQuery(as_new)
{
    let url = document.getElementById('store_url').value;

    let data = internalFormData();
    data.append('set', props.set.id);
    data.append('title', currentQuery.title);
    data.append('description', currentQuery.description);
    data.append('query', currentQuery.query);

    if (as_new == false) {
        data.append('id', currentQuery.id);
    }

    return fetch(url, {
        method: 'POST',
        body: data,
    }).then((res) => res.json());
}

function newQuery()
{
    if (editing) {
        currentNewQuery = {
            id: randomString(10),
            title: 'Unnamed query',
            description: 'Undescribed query',
            query: '',
            active: true,
            local: true,
        };

        newModal.show();
    }
}

function saveNewQuery()
{
    if (editing) {
        newModal.hide();
        allQueries.push(currentNewQuery);
        currentQuery = currentNewQuery;
        let query = currentQueryObj();
        selectQuery(query);
    }
}

function currentQueryObj()
{
    return allQueries.find((q) => q.id == currentQuery.id);
}

function deleteQuery()
{
    let cur = currentQueryObj();

    if (cur.local != true) {
        let url = document.getElementById('delete_url').value;

        let data = internalFormData();
        data.append('_method', 'DELETE');
        data.append('set', props.set.id);
        data.append('id', cur.id);

        fetch(url, {
            method: 'POST',
            body: data,
        });
    }

    allQueries = allQueries.filter((q) => q.id != cur.id);

    if (allQueries.length > 0) {
        selectQuery(allQueries[0]);
    }
    else {
        selectQuery({
            id: '',
            title: '',
            description: '',
            query: '',
            active: true,
            local: true,
        });

        newQuery();
    }
}

async function saveQuery()
{
    saving = true;

    let cur = currentQueryObj();
    let response = await pushQuery(cur.local == true);

    cur.id = response.id;
    cur.title = response.title;
    cur.description = response.description;
    cur.query = response.query;
    cur.local = false;

    saving = false;
}

function copyLink(ev, type)
{
    let url = props.set.canonical_link + '#q=' + currentQuery.id;
    if (type == 'results') {
        url += '&exec=1';
    }

    if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then((ev) => {
            ev.currentTarget.classList.append('copied');
            setTimeout(() => {
                ev.currentTarget.classList.remove('copied');
            }, 500);
        });
    }
    else {
        modalLink = url;
        copyModal.show();
    }
}

onMount(() => {
    copyModal = new bootstrap.Modal(document.getElementById('copyModal'));
    newModal = new bootstrap.Modal(document.getElementById('newModal'));

    let extensions = [
        basicSetup,
        lineNumbers(),
        bracketMatching(),
        sparql(),

        /*
            To sync the content of the CodeMirror editor with the "query" rune.
            Reminder: this is required also when not in editing mode, as the
            state of the rune drives the state of "Execute" button
        */
        EditorView.updateListener.of((update) => {
            if (update.docChanged) {
                currentQuery.query = update.state.doc.toString();
            }
        })
    ];

    if (editing == false) {
        extensions.push(EditorState.readOnly.of(true));
        extensions.push(EditorView.editable.of(false));
    }

    editor = new EditorView({
        extensions: extensions,
        parent: document.querySelector('#editor'),
    });

    if (allQueries.length > 0) {
        let selected = false;

        /*
            TODO:
            add button to obtain direct link
            - to query: hash q=id
            - to results: hash q=id&exec=1

            Add an "embed" mode
        */
        if (window.location.hash) {
            const params = new URLSearchParams(window.location.hash.substr(1));
            const selected_query_id = params.get('q');
            if (selected_query_id) {
                allQueries.every((q) => {
                    if (q.id == selected_query_id) {
                        selectQuery(q);
                        selected = true;

                        const exec = params.get('exec');
                        if (exec == '1') {
                            executeQuery();
                        }

                        return false;
                    }

                    return true;
                })
            }
        }

        if (selected == false) {
            selectQuery(allQueries[0]);
        }
    }
    else {
        newQuery();
    }

    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((node) => {
        new bootstrap.Tooltip(node);
    });
});

</script>

<div class="collection-editor">
    <div class="modal fade" id="copyModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Copy Your Link</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <textarea class="form-control" readonly>{modalLink}</textarea>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="newModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add New Query</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-3">
                        <label for="new-title" class="form-label">Title</label>
                        <input id="new-title" type="text" class="form-control" name="title" bind:value={currentNewQuery.title}>
                    </div>
                    <div>
                        <label for="new-description" class="form-label">Description</label>
                        <input id="new-description" type="text" class="form-control" name="description" bind:value={currentNewQuery.description}>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-success" onclick={() => saveNewQuery()}>Save</button>
                </div>
            </div>
        </div>
    </div>

    <div class="card">
        <div class="card-body">
            <div class="row edit-section">
                <div class="col-12 col-md-3 h-100 d-flex flex-column nav-side">
                    {#if editing}
                        <button aria-label="New" type="button" class="btn btn-success d-block w-100 mb-2" onclick={newQuery}><i class="bi bi-plus-circle"></i> New query</button>
                    {/if}

                    <div class="list-group h-100 overflow-auto">
                        {#if allQueries.length == 0}
                            {#if editing}
                                <div class="alert alert-info">
                                    Click "New Query" to init a new element in this Collection.
                                </div>
                            {:else}
                                <div class="alert alert-info">
                                    This collection is empty.
                                </div>
                            {/if}
                        {:else}
                            {#each allQueries as iquery}
                                <button class="list-group-item list-group-item-action { iquery.active && 'active' }" onclick={() => selectQuery(iquery)}>
                                    <h5>{ iquery.title == '' ? 'Unnamed query' : iquery.title }</h5>
                                    {#if iquery.description != ''}
                                        <p class="m-0">{ iquery.description }</p>
                                    {/if}
                                </button>
                            {/each}
                        {/if}
                    </div>
                </div>
                <div class="col-12 col-md-9 edit-col min-vh-50-on-mobile">
                    {#if editing}
                        <div class="mb-3 row">
                            <label for="query-title" class="col-sm-2 col-form-label">Title</label>
                            <div class="col-sm-10">
                                <input type="text" class="form-control" id="query-title" bind:value={currentQuery.title}>
                            </div>
                        </div>

                        <div class="mb-3 row">
                            <label for="query-desc" class="col-sm-2 col-form-label">Description</label>
                            <div class="col-sm-10">
                                <input type="text" class="form-control" id="query-desc" bind:value={currentQuery.description}>
                            </div>
                        </div>

                        <div class="mb-3 row">
                            <div class="col-sm-10 offset-sm-2 d-flex justify-content-between">
                                <div class="btn-group" role="group">
                                    <button type="button" class="btn btn-success" disabled={currentQuery.query == ''} onclick={() => saveQuery()}>
                                        {#if saving}
                                            <div class="spinner-border spinner-border-sm" role="status">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                        {:else}
                                            <i class="bi bi-save"></i> Save
                                        {/if}
                                    </button>

                                    <button type="button" class="btn btn-danger" onclick={deleteQuery}><i class="bi bi-trash"></i> Delete Query</button>
                                </div>

                                <div class="btn-group" role="group">
                                    <button type="button" class="btn btn-success" disabled={currentQuery.query == ''} onclick={executeQuery}><i class="bi bi-play"></i> Execute</button>
                                    <button type="button" class="btn btn-danger" disabled={!running} onclick={stopExecution}><i class="bi bi-stop-fill"></i> Stop</button>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="text-end mb-3">
                            <div class="btn-group" role="group">
                                <button type="button" class="btn btn-success" disabled={currentQuery.query == ''} onclick={executeQuery}><i class="bi bi-play"></i> Execute</button>
                                <button type="button" class="btn btn-danger" disabled={!running} onclick={stopExecution}><i class="bi bi-stop-fill"></i> Stop</button>

                                <div class="btn-group" role="group">
                                    <button type="button" class="btn btn-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">Share</button>
                                    <ul class="dropdown-menu">
                                        <li><a class="dropdown-item" href="#" onclick={(e) => copyLink(e, 'query')}>Link to query</a></li>
                                        <li><a class="dropdown-item" href="#" onclick={(e) => copyLink(e, 'results')}>Link to results</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    {/if}

                    <div class="text-box border rounded p-2" id="editor">
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="result-box">
        {#key currentResults}
            <QueryResult results={currentResults} error={currentError} query={currentQuery.query} />
        {/key}
    </div>
</div>
